import { FC, Fragment, useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  PaginationProps,
  Skeleton,
  Tooltip,
  message,
} from "antd";
import {
  CloseOutlined,
  DownloadOutlined,
  LeftOutlined,
  PlayCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import ErrorBoundary from "modules/shared/components/ErrorBoundary";
import PageLoader from "modules/shared/components/PageLoader";
import Paginator from "modules/shared/components/Paginator";
import { usePageTitle } from "modules/shared/hooks";
import { useOrganizationRoutes } from "routes/organizationRoute";
import { useProjectDetailsStore } from "modules/organization/store";
import { useRankingProjectListStore } from "modules/organization/store/rankingProjectListStore";
import { mnDate } from "modules/shared/services";
import { dateFormat } from "config/commonConfig";
import { downloadBufferAsFile } from "modules/shared/utility";
import { RankingProjectService } from "modules/organization/services/rankingProject.service";
import { useRankingSnapshotStore } from "./store/rankingSnapshotStore";
import RankingSnapshotTableView from "./components/RankingSnapshotTableView";
import MedalWinningQuestionsDrawer from "./components/MedalWinningQuestionsDrawer";
import DeleteRankingSnapshot from "./components/DeleteRankingSnapshot";
import "./ranking-snapshot.css";

const { Search } = Input;
const DEFAULT_SKELETON_SUMMARY_CARDS = 4;

const OrgRankingSnapshotPage: FC = () => {
  const { ranking_project_id } = useParams<{ ranking_project_id: string }>();
  const { projectDetails, isLoadingProjectDetails } = useProjectDetailsStore();
  const rankingProjects = useRankingProjectListStore(
    (state) => state.rankingProjects,
  );
  const setRankingProjectRunningState = useRankingProjectListStore(
    (state) => state.setRankingProjectRunningState,
  );
  const currentRankingProject = rankingProjects?.find(
    (project) => project.id === Number(ranking_project_id),
  );
  const rankingProjectTitle = currentRankingProject?.title;
  const [isTriggeringRun, setIsTriggeringRun] = useState(false);
  const { organizationBaseRouteUrl } = useOrganizationRoutes();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    header,
    rows,
    isLoading,
    count,
    pageIndex,
    pageSize,
    selectedRanks,
    selectedScores,
    searchTerm,
    loadRankingSnapshot,
    setSearch,
    toggleRankFilter,
    toggleScoreFilter,
    setPage,
    setPageSize,
    auditList,
    isLoadingAuditList,
    selectedSnapshotId,
    isDeletingSnapshotId,
    selectSnapshot,
  } = useRankingSnapshotStore();

  const projectSlug = projectDetails?.slug ?? "";
  const rankingProjectId = Number(ranking_project_id);

  usePageTitle(
    `MonQcle - ${
      projectDetails
        ? projectDetails?.title + " - Ranking Snapshot"
        : "Ranking Snapshot"
    }`,
  );

  const skipNextUrlSyncRef = useRef(true);
  const skipNextScoreUrlSyncRef = useRef(true);

  useEffect(() => {
    skipNextUrlSyncRef.current = true;
    skipNextScoreUrlSyncRef.current = true;
    if (!projectDetails?.slug || !ranking_project_id) {
      return;
    }
    const initialRankFilter = (searchParams.get("rank_filter") ?? "")
      .split("-")
      .filter(Boolean);
    const initialScoreFilter = (searchParams.get("score_filter") ?? "")
      .split("-")
      .filter(Boolean)
      .map(Number)
      .filter((value) => !Number.isNaN(value));
    loadRankingSnapshot(
      projectDetails.slug,
      Number(ranking_project_id),
      initialRankFilter,
      initialScoreFilter,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRankingSnapshot, projectDetails?.slug, ranking_project_id]);

  useEffect(() => {
    if (skipNextUrlSyncRef.current) {
      skipNextUrlSyncRef.current = false;
      return;
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (selectedRanks.length > 0) {
          next.set("rank_filter", selectedRanks.join("-"));
        } else {
          next.delete("rank_filter");
        }
        return next;
      },
      { replace: true },
    );
  }, [selectedRanks, setSearchParams]);

  useEffect(() => {
    if (skipNextScoreUrlSyncRef.current) {
      skipNextScoreUrlSyncRef.current = false;
      return;
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (selectedScores.length > 0) {
          next.set("score_filter", selectedScores.join("-"));
        } else {
          next.delete("score_filter");
        }
        return next;
      },
      { replace: true },
    );
  }, [selectedScores, setSearchParams]);

  const [isExporting, setIsExporting] = useState(false);
  const [deletingSnapshot, setDeletingSnapshot] = useState<{
    snapshotId: number;
    executedAtLabel: string;
  } | null>(null);
  const [projectDateRange, setProjectDateRange] = useState<{
    effectiveFrom: string;
    throughTo: string;
  } | null>(null);
  const [isLoadingDateRange, setIsLoadingDateRange] = useState(true);

  useEffect(() => {
    if (!projectSlug || !rankingProjectId) {
      return;
    }
    let isCancelled = false;
    setIsLoadingDateRange(true);
    const loadProjectDateRange = async () => {
      try {
        const { data: projectsResponse } =
          await new RankingProjectService().getRankingProjects(projectSlug);
        const currentProject =
          projectsResponse.data?.ranking_projects?.find(
            (project) => project.id === rankingProjectId,
          ) ?? null;
        if (!currentProject) {
          return;
        }
        let effectiveFrom = currentProject.effective_from;
        let throughTo = currentProject.through_to;
        if (currentProject.include_all_records) {
          const { data: rangeResponse } =
            await new RankingProjectService().getProjectRecordDateRange(
              projectSlug,
            );
          effectiveFrom =
            rangeResponse.data?.record_date_range?.min_effective_from ?? null;
          throughTo =
            rangeResponse.data?.record_date_range?.max_through_to ?? null;
        }
        if (!isCancelled && effectiveFrom && throughTo) {
          setProjectDateRange({
            effectiveFrom: mnDate(effectiveFrom).format(dateFormat.default),
            throughTo: mnDate(throughTo).format(dateFormat.default),
          });
        }
      } catch {
        // Non-critical display data — fail silently rather than blocking the page.
      } finally {
        if (!isCancelled) {
          setIsLoadingDateRange(false);
        }
      }
    };
    loadProjectDateRange();
    return () => {
      isCancelled = true;
    };
  }, [projectSlug, rankingProjectId]);

  const handleRunRanking = async () => {
    if (isTriggeringRun || currentRankingProject?.is_running) {
      return;
    }
    setIsTriggeringRun(true);
    try {
      await new RankingProjectService().runRanking(
        projectSlug,
        rankingProjectId,
      );
      setRankingProjectRunningState(rankingProjectId, true);
      message.success("Ranking execution started successfully.");
    } catch (error) {
      console.error(error);
      message.error("Failed to start ranking execution.");
    } finally {
      setIsTriggeringRun(false);
    }
  };

  const exportRankingSnapshot = async () => {
    const exportToastKey = "exportRankingSnapshot";
    message.loading({
      key: exportToastKey,
      content: "Downloading Ranking Snapshot...",
      duration: 0,
    });
    setIsExporting(true);
    try {
      const { data } = await new RankingProjectService().exportRankingSnapshot(
        projectSlug,
        rankingProjectId,
        selectedSnapshotId,
      );
      const { fileData } = data.data;
      if (fileData) {
        const rankingTypePrefix =
          header?.ranking_type === "point"
            ? "Point_Base_Ranking_Of"
            : "Template_Base_Ranking_Of";
        const sanitizedTitle = (projectDetails?.title ?? "Ranking")
          .trim()
          .replace(/\s+/g, "_");
        const fileName = `${rankingTypePrefix}_${sanitizedTitle}_Snapshot_${mnDate().format(dateFormat.fileTimestamp)}.csv`;
        downloadBufferAsFile(fileData, fileName, "application/octet-stream");
      }
      message.success({
        key: exportToastKey,
        content: "Ranking Snapshot downloaded successfully.",
      });
    } catch (error: any) {
      const backendMessage = error?.error?.response?.data?.message;
      const contentMessage =
        !backendMessage && error instanceof Error ? error.message : null;
      message.error({
        key: exportToastKey,
        content:
          backendMessage ||
          contentMessage ||
          "Failed to download Ranking Snapshot.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const formatAuditTimestamp = (executedAt: string) =>
    mnDate(executedAt).format(dateFormat.tableDateTime);

  const getShowingDataString = () => {
    const startIndex = (pageIndex - 1) * pageSize + 1;
    const endIndex =
      count < pageIndex * pageSize ? count : pageIndex * pageSize;
    return `Showing ${startIndex} to ${endIndex} of ${count} Items`;
  };

  const paginationRender: PaginationProps["itemRender"] = (
    _,
    type,
    originalElement,
  ) => {
    if (type === "prev") {
      return <span>Previous</span>;
    }
    if (type === "next") {
      return <span>Next</span>;
    }
    return originalElement;
  };

  const summaryCardCount = header?.medal_summary
    ? header.medal_summary.length
    : DEFAULT_SKELETON_SUMMARY_CARDS;

  return (
    <ErrorBoundary>
      <Fragment>
        {isLoadingProjectDetails ? (
          <PageLoader />
        ) : (
          <Fragment>
            {projectDetails && (
              <Fragment>
                <div className="pageTopSection">
                  <div className="backLink">
                    <Link
                      to={
                        organizationBaseRouteUrl +
                        `/project/${projectDetails?.slug}/ranking-projects`
                      }
                    >
                      <LeftOutlined role="img" aria-label="" />
                      Back to Ranking Projects
                    </Link>
                  </div>
                </div>
                <div className="pageHeadingWrap snapshotHeadingWrap noMargin">
                  <div className="pageHeadingWrap">
                    {/* <h2 id="tab-header">Ranking Snapshot</h2> */}
                    <h2
                      className="displayFlex ellipsisTxt"
                      style={{ alignItems: "baseline" }}
                    >
                      <Tooltip
                        placement="right"
                        trigger={["hover"]}
                        title={projectDetails?.title ?? ""}
                        overlayStyle={{ maxWidth: "40%" }}
                      >
                        <p
                          className="ellipsisTxt"
                          style={{ maxWidth: "40%", marginBottom: 0 }}
                        >
                          {projectDetails?.title}
                        </p>
                      </Tooltip>
                      {rankingProjectTitle && (
                        <Fragment>
                          &nbsp;{"|"}&nbsp;
                          <Tooltip
                            placement="right"
                            trigger={["hover"]}
                            title={rankingProjectTitle}
                            overlayStyle={{ maxWidth: "40%" }}
                          >
                            <p
                              className="ellipsisTxt"
                              style={{ maxWidth: "40%", marginBottom: 0 }}
                            >
                              {rankingProjectTitle}
                            </p>
                          </Tooltip>
                        </Fragment>
                      )}
                      &nbsp;{"|"} Ranking Snapshot{" "}
                    </h2>
                  </div>
                </div>
                <div className="snapshotLayoutWrap">
                  <div className="snapshotMainCol">
                    {isLoading && !header ? (
                      <Skeleton.Input
                        active
                        size="small"
                        style={{ width: 100 }}
                      />
                    ) : (
                      header?.last_execution && (
                        <div className="snapshotExecutedAtRow">
                          <div className="snapshotExecutedAtWrap">
                            {isLoadingDateRange ? (
                              <span className="snapshotProjectDateRange">
                                Effective:{" "}
                                <span className="snapshotDateSkeletonBar" /> -
                                Through:{" "}
                                <span className="snapshotDateSkeletonBar" />
                              </span>
                            ) : (
                              projectDateRange && (
                                <span className="snapshotProjectDateRange">
                                  Effective: {projectDateRange.effectiveFrom} -
                                  Through: {projectDateRange.throughTo}
                                </span>
                              )
                            )}
                            <span className="snapshotExecutedAt">
                              {mnDate(header.last_execution.executed_at).format(
                                dateFormat.tableDate,
                              )}
                            </span>
                          </div>
                          <div className="snapshotHeaderActions">
                            <Tooltip
                              title={
                                isTriggeringRun
                                  ? "Starting..."
                                  : currentRankingProject?.is_running
                                    ? "Running"
                                    : "Run Ranking"
                              }
                            >
                              <Button
                                className="iconOnlyBtn smallIcon"
                                shape="circle"
                                aria-label={
                                  isTriggeringRun
                                    ? "Starting"
                                    : currentRankingProject?.is_running
                                      ? "Running"
                                      : "Run Ranking"
                                }
                                disabled={
                                  isTriggeringRun ||
                                  currentRankingProject?.is_running
                                }
                                loading={isTriggeringRun}
                                onClick={handleRunRanking}
                              >
                                {!isTriggeringRun &&
                                  (currentRankingProject?.is_running ? (
                                    <SyncOutlined role="img" aria-hidden spin />
                                  ) : (
                                    <PlayCircleOutlined
                                      role="img"
                                      aria-hidden
                                    />
                                  ))}
                              </Button>
                            </Tooltip>
                            <Tooltip title="Export Ranking Snapshot as CSV">
                              <Button
                                className="snapshotExportBtn"
                                aria-label="Export Ranking Snapshot as CSV"
                                icon={<DownloadOutlined />}
                                loading={isExporting}
                                disabled={isExporting}
                                onClick={exportRankingSnapshot}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      )
                    )}
                    {((isLoading && !header) ||
                      (header?.medal_summary?.length ?? 0) > 0) && (
                      <div className="snapshotSummaryWrap">
                        {isLoading && !header
                          ? Array.from(
                              { length: summaryCardCount },
                              (_, index) => (
                                <div
                                  className="snapshotSummaryCard"
                                  key={`skeleton-summary-${index}`}
                                >
                                  <span className="summaryLabel">
                                    <Skeleton.Input
                                      active
                                      size="small"
                                      style={{ width: 70 }}
                                    />
                                  </span>
                                  <span className="summaryCount">
                                    <Skeleton.Input
                                      active
                                      size="small"
                                      style={{ width: 40 }}
                                    />
                                  </span>
                                </div>
                              ),
                            )
                          : header?.medal_summary?.map((summary) => {
                              const isTemplateType =
                                header?.ranking_type === "template";
                              const isFilterable = true;
                              const isSelected = isTemplateType
                                ? selectedRanks.includes(summary.rank_name)
                                : selectedScores.includes(summary.count);
                              const isDisabled =
                                isTemplateType && summary.count === 0;
                              const cardClassName = [
                                "snapshotSummaryCard",
                                isFilterable && isSelected ? "selected" : "",
                                isFilterable && isDisabled && !isSelected
                                  ? "disabled"
                                  : "",
                              ]
                                .filter(Boolean)
                                .join(" ");

                              const isClickable =
                                isFilterable && (!isDisabled || isSelected);
                              const activateFilter = () => {
                                if (!isClickable) {
                                  return;
                                }
                                if (isTemplateType) {
                                  toggleRankFilter(
                                    projectSlug,
                                    rankingProjectId,
                                    summary.rank_name,
                                  );
                                } else {
                                  toggleScoreFilter(
                                    projectSlug,
                                    rankingProjectId,
                                    summary.count,
                                  );
                                }
                              };
                              return (
                                <div
                                  className={cardClassName}
                                  key={summary.rank_name}
                                  role={isClickable ? "button" : undefined}
                                  tabIndex={isClickable ? 0 : undefined}
                                  aria-pressed={
                                    isClickable ? isSelected : undefined
                                  }
                                  aria-label={
                                    isFilterable
                                      ? `Filter by ${summary.rank_name}`
                                      : undefined
                                  }
                                  onClick={activateFilter}
                                  onKeyDown={(e) => {
                                    if (!isClickable) {
                                      return;
                                    }
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      activateFilter();
                                    }
                                  }}
                                >
                                  <span className="summaryLabel">
                                    {summary.rank_name}
                                  </span>
                                  <span className="summaryCount">
                                    {summary.count}
                                  </span>
                                </div>
                              );
                            })}
                      </div>
                    )}
                    <div className="tableSettingBlk">
                      <Search
                        aria-label="Search Ranking Snapshot"
                        placeholder="Search jurisdiction, series"
                        allowClear
                        defaultValue=""
                        onChange={(e) => {
                          if (isLoading) {
                            return;
                          }
                          if (e.target.value === "" && searchTerm !== "") {
                            setSearch(projectSlug, rankingProjectId, "");
                          }
                        }}
                        onSearch={(value) => {
                          if (isLoading) {
                            return;
                          }
                          const trimmedValue = value.trim();
                          if (trimmedValue === searchTerm) {
                            return;
                          }
                          setSearch(
                            projectSlug,
                            rankingProjectId,
                            trimmedValue,
                          );
                        }}
                      />
                    </div>
                    <ErrorBoundary>
                      <RankingSnapshotTableView
                        projectSlug={projectSlug}
                        rankingProjectId={rankingProjectId}
                        rows={rows}
                        loading={isLoading}
                        skeletonRowCount={pageSize}
                        rankingType={header?.ranking_type}
                        onOpenRecord={(record) =>
                          navigate(
                            organizationBaseRouteUrl +
                              `/project/${projectDetails?.slug}/record/${record.record_id}`,
                          )
                        }
                      />
                    </ErrorBoundary>
                    <Paginator
                      disabled={isLoading}
                      currentPageSize={pageSize}
                      count={count}
                      setCurrentPageSize={(value: number) =>
                        setPageSize(projectSlug, rankingProjectId, value)
                      }
                      setCurrentPage={(page: number) =>
                        setPage(projectSlug, rankingProjectId, page)
                      }
                      getShowingDataString={getShowingDataString}
                      paginationRender={paginationRender}
                      currentPage={pageIndex}
                    />
                  </div>
                  <div className="snapshotAsideCol">
                    <h3>Audit History</h3>
                    {isLoadingAuditList && auditList.length === 0 ? (
                      <ul className="snapshotAuditList">
                        {Array.from({ length: 3 }, (_, index) => (
                          <li
                            className="snapshotAuditEntry"
                            key={`audit-skeleton-${index}`}
                          >
                            <Skeleton.Input
                              active
                              size="small"
                              style={{ width: "100%" }}
                            />
                          </li>
                        ))}
                      </ul>
                    ) : auditList.length === 0 ? (
                      <p className="snapshotAuditEmpty">
                        No execution history yet.
                      </p>
                    ) : (
                      <ul className="snapshotAuditList">
                        {auditList.map((entry) => {
                          const isSelected =
                            selectedSnapshotId === null
                              ? entry.snapshot_id === auditList[0].snapshot_id
                              : selectedSnapshotId === entry.snapshot_id;
                          return (
                            <li
                              key={entry.snapshot_id}
                              className={
                                isSelected
                                  ? "snapshotAuditEntry selected"
                                  : "snapshotAuditEntry"
                              }
                            >
                              <button
                                type="button"
                                className="snapshotAuditEntryBtn"
                                aria-pressed={isSelected}
                                onClick={() =>
                                  selectSnapshot(
                                    projectSlug,
                                    rankingProjectId,
                                    entry.snapshot_id,
                                  )
                                }
                              >
                                {formatAuditTimestamp(entry.executed_at)}
                              </button>
                              <Button
                                className="snapshotAuditDeleteBtn"
                                type="text"
                                size="small"
                                aria-label={`Delete snapshot from ${formatAuditTimestamp(
                                  entry.executed_at,
                                )}`}
                                icon={<CloseOutlined role="img" aria-hidden />}
                                loading={
                                  isDeletingSnapshotId === entry.snapshot_id
                                }
                                disabled={
                                  isDeletingSnapshotId === entry.snapshot_id
                                }
                                onClick={() =>
                                  setDeletingSnapshot({
                                    snapshotId: entry.snapshot_id,
                                    executedAtLabel: formatAuditTimestamp(
                                      entry.executed_at,
                                    ),
                                  })
                                }
                              />
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
                <MedalWinningQuestionsDrawer />
                <DeleteRankingSnapshot
                  open={deletingSnapshot !== null}
                  projectSlug={projectSlug}
                  rankingProjectId={rankingProjectId}
                  snapshotId={deletingSnapshot?.snapshotId ?? null}
                  executedAtLabel={deletingSnapshot?.executedAtLabel ?? ""}
                  onClose={() => setDeletingSnapshot(null)}
                />
              </Fragment>
            )}
          </Fragment>
        )}
      </Fragment>
    </ErrorBoundary>
  );
};

export default OrgRankingSnapshotPage;
